import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  Type,
  ViewChild,
  ViewChildren,
  QueryList
} from '@angular/core';
import {first} from 'rxjs/operators';
import {SvgIcon} from '@valg-shared/utils/svg-icon';
import {WithPropsOfType} from '@valg-shared/forms';
import {Column} from '@valg-shared/components/table/table/column';
import {ItemOption} from '@valg-shared/components/table/table/item-option';
import {ColumnMetadata} from '@valg-shared/components/table/table/column-metadata';
import {Sort} from './valg-sort.directive';
import {ValgDatasource} from './valg-datasource';
import {Pagination, ValgPaginatorComponent} from '@valg-shared/components/table/table/valg-paginator/valg-paginator.component';
import {ValgFilterDialogComponent} from '@valg-shared/components/table/table/valg-filter-dialog/valg-filter-dialog.component';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {HeaderMetatada} from '@valg-shared/services/export-service/header-metadata';
import {FilterStateService} from '@valg-shared/services/filter-service/filter-state.service';
import {CellRendererComponent} from '@valg-shared/components/table/directives/cell-component-outlet/cell-component-outlet.directive';
import {getValueAt} from '@valg-shared/utils/object-utils';
import {TranslateService} from "@ngx-translate/core";
import {camelToSnakeCase} from "../../../utils/string-utils";
import {
  TextEllipsisTooltipRendererComponent
} from '@valg-shared/components/table/renderers/text-ellipsis-tooltip-renderer/text-ellipsis-tooltip-renderer.component';
import {TableFilterDialogColumnData, TableFilterDialogData, TableFilterDialogResult} from "./valg-filter-dialog/table-filter-dialog.data";
import {DelayedExecutor} from "../../../utils/helpers";
import {ValgDialogService} from '@valg-shared/services/dialog/valg-dialog.service';
import {cprPattern} from "../../../utils/patterns";
import { BarcodeScanner } from '@valg-shared/utils/barcode-scanner';
import { OverflowMenuComponent } from '../../overflow-menu/overflow-menu.component';
import { Subscription, fromEvent, lastValueFrom } from 'rxjs';

let TABLE_ID = 0;

export enum TableSecondaryAction {
  VALUE_FILTER,
  FULL_SCREEN,
  EXPORT,
  PRINT,
  SEARCH,
  EXPORT_PDF,
  REFRESH
}

export enum SearchWidth {
  Normal,
  Medium,
  Wide
}

export interface TableDataItem {
  id: string | number;
  children?: TableDataItem[];
  loadingStatus?: TableDataItemLoadingStatus;
}

export enum TableDataItemLoadingStatus {
  NotLoaded= "NotLoaded",
  Loading= "Loading",
  Done= "Done"
}

export enum TableQueryChangeEventType {
  Initializer = "Initializer",
  Filter = "Filter",
  Paginator = "Paginator",
  Sorter = "Sorter",
  Searcher = "Searcher"
}

export interface TableQueryResult {
  paginator: TablePaginator
  filterResult?: TableFilterDialogResult,
  sort?: Sort,
  searchValues?: { string: string; };
}

export interface TablePaginator {
  pageSize: number,
  pageNumber: number
}

export interface TableSearchField {
  label: string,
  discriminator: string,
  searchableColumnKeys?: string[],
  formatter?: (value: string) => string,
  barcodeScannerProcessing?: (scanned: string) => string | undefined, 
  barcodePatchTarget?: string,
}

export interface TableQueryChangeEvent {
  tableQueryResult: TableQueryResult,
  eventType: TableQueryChangeEventType,
}

@Component({
  selector: 'valg-table',
  templateUrl: './table.component.html',
  styleUrls: [ './table.component.scss' ],
})
export class TableComponent<T extends TableDataItem> implements OnInit, OnChanges, AfterViewInit {
  @Input()
  useFooterRow = false;

  @Input()
  useHeaderRow = true;

  @Input()
  showPageSizeMenu = true;

  @Input()
  footerData: T | undefined;

  @Input()
  columnDefinition!: WithPropsOfType<T, ColumnMetadata>;

  @Input()
  data: T[] = [];

  @Input()
  itemsCountFunction?: (data: T[]) => number;

  @Input()
  noDataMessage = "";

  @Input()
  shouldHaveItemOption = false;

  @Input()
  itemOptions: ItemOption<T>[] = [];

  @Input()
  dragDropEnabled = false;

  @Input()
  itemPerPage = 10;

  @Input()
  totalItems: number | undefined;

  title = '';

  @Input()
  filterDialogDescription?: string;

  @Input()
  itemName = '';

  @Input()
  description!: string;

  @Input()
  childItemName?: string;

  @Input()
  pluralChildItemName?: string;

  @Input()
  displayItemCount = true;

  @Input()
  enableFiltering = false;

  @Input()
  enableExport = false;

  @Input()
  exportButtonText?: string;

  @Input()
  exportFileName?: string;

  @Input()
  mainActionName = '';

  @Input()
  secondaryActionName = '';

  @Input()
  lazySearch = false;

  @Input()
  lazyLoad = false;

  @Input()
  searchPlaceHolder?: string;

  @Input()
  searchFields: TableSearchField[] = []

  @Input()
  printButtonLabel!: string;

  @Input()
  hasSearchBox = false;

  @Input()
  hasPagination = true;

  @Input()
  hasMainActionButton = true;

  @Input()
  hasSecondaryActionButton = false;

  @Input()
  useSecondaryButton = false;

  @Input()
  exportFunction?: () => Promise<T[]> | T[];

  @Input() hasRowChanged?: (row: T) => boolean;

  @Input() hasLiveUpdates = false;

  @Input()
  canExportHeadersOnly = false;

  @Input()
  hasMainActionButtonDisabled = false;

  @Input()
  hasSecondaryActionButtonDisabled = false;

  @Input()
  hasTitle = true;

  @Input()
  showChildrenRowsCount = false;

  @Input()
  externalHeaderDataForExport?: HeaderMetatada[];

  @Input()
  externalPagination = false;

  @Input()
  shouldKeepStateWhenDataChange = false;

  @Input()
  showPrintButton = false;

  @Input()
  showRefreshButton = false;

  @Input()
  refreshButtonText = '';

  @Input()
  columnSpacing = false;

  @Input()
  enableExportToPdf = false;

  @Input()
  exportButtonIcon?: string;

  @Input()
  searchWidth = 0;

  @Input()
  ariaRelevant = "text";

  @Input()
  enableBorder = false;

  @Input()
  customFilterTitle?: string;

  @Input()
  customMainButtonTitle?: string;

  @Input()
  shouldDiplayItemNameOnTotal = true

  @Output()
  printHandlerEmitter: EventEmitter<T[]> = new EventEmitter<T[]>();

  @Output()
  mainActionEventEmitter: EventEmitter<unknown> = new EventEmitter<unknown>();

  @Output()
  secondaryActionEventEmitter: EventEmitter<unknown> = new EventEmitter<unknown>();

  @Output()
  queryChangeEvent = new EventEmitter<TableQueryChangeEvent>();

  @Output()
  dragDropDataChanged: EventEmitter<T[]> = new EventEmitter<T[]>();

  @Output()
  refreshEventEmitter: EventEmitter<void> = new EventEmitter<void>();

  @Output() filterChangeEmitter = new EventEmitter<TableFilterDialogResult>();

  @ViewChild('tableTitleContainer') _tableTitleContainer!: ElementRef;
  @ViewChild('tableContainer') _tableContainer!: ElementRef;
  @ViewChild('tablePaginator') _tablePaginator!: ValgPaginatorComponent<T>;

  @ViewChildren('searchInputs') searchInputs?: QueryList<ElementRef> = undefined;

  itemCount = 0;
  currentPage = 0;

  columns: Column[] = [];
  dataHeaders: string[] = [];
  columnNames: string[] = [];
  private _tableQuery?: TableQueryResult

  dragDisabled = true;

  itemIndexToHoldFocus?: number;
  textNoSearchResults?: string;
  textNoInitialDataResults?: string;
  public id = `valg-table-${TABLE_ID++}`;

  public optionColumn: Column = {
    title: 'table.option_column',
    path: '',
  };
  public dragColumn: Column = {
    title: ' ',
    path: '',
  };
  public expansionColumn: Column = {
    title: 'expansionColumn',
    path: '',
  };

  public SvgIcon = SvgIcon;
  dataSource: ValgDatasource<T>;
  public secondaryCounts: Map<string, number> = new Map<string, number>();
  public primaryCount = 0;

  private barCodeScanner: BarcodeScanner = new BarcodeScanner();
  private barcodeScannerSubscription?: Subscription = undefined;

  public latestSort?: Sort;
  public dragDropMoveDownText = 'table.drag_drop_move_down'
  public dragDropMoveUpText = 'table.drag_drop_move_up'

  private latestFilter: TableFilterDialogResult | undefined;

  private _secondaryActions: TableSecondaryAction[] = [];
  private _afterViewInitialized = false;
  private _delayedSearch = DelayedExecutor();

  @Input()
  public searchUsed: boolean = false;

  @Input()
  filterKey?: string;

  constructor(public dialog: ValgDialogService,
              private readonly renderer: Renderer2,
              private readonly cdr: ChangeDetectorRef,
              private readonly translateService: TranslateService,
              private readonly filterStateService: FilterStateService,
              private readonly ngZone: NgZone
  ) {
    this.dataSource = new ValgDatasource<T>();
  }

  ngAfterViewInit() {
    this.title = this._tableTitleContainer?.nativeElement.querySelector("h1, h2, h3, h4, h5, h6")?.textContent;
    this._afterViewInitialized = true;
    if (this.externalPagination) {
      this.constructDefaultQuery();
    }

  }

  async ngOnInit(): Promise<void> {
    this.dataSource.dataStream.subscribe((v) => {
      this.itemCount = v.length;
      this.primaryCount = this.itemsCountFunction
          ? this.itemsCountFunction(v)
          : v.filter((item: T) => item.children != null).length;
      this.secondaryCounts = this.dataSource.calculateSecondaryCount();
    });
    this._secondaryActions = [];

    if (this.enableFiltering) {
      this._secondaryActions.push(TableSecondaryAction.VALUE_FILTER);
    }
    if (this.enableExport) {
      this._secondaryActions.push(TableSecondaryAction.EXPORT);
    }
    if(this.hasSearchBox) {
      this._secondaryActions.push(TableSecondaryAction.SEARCH);
    }
    if(this.enableExportToPdf) {
      this._secondaryActions.push(TableSecondaryAction.EXPORT_PDF);
    }
    if(this.showRefreshButton) {
      this._secondaryActions.push(TableSecondaryAction.REFRESH);
    }
    if(this.externalPagination) {
      this.dataSource.disableDataManipulation();
    }
    

    await this.updateNoDataMessages();
  }

  applyFilter(filter: TableFilterDialogResult | undefined) {
  if (filter === undefined) {
    this.applyEmptyFilter();
    return;
  }
  this.latestFilter = filter;
  this.searchUsed = !!filter;
  this.filterChangeEmitter.emit(filter);
  this.dataSource.updateFilterInformation(filter);
  if (this.filterStateService.isValidKey(this.filterKey)) {
    this.filterStateService.saveFilter(this.filterKey, filter);
  }
  if (this.externalPagination) {
    this._tableQuery = { ...this._tableQuery, filterResult: filter } as TableQueryResult;
    this.emitQueryChangeEvent(this._tableQuery, TableQueryChangeEventType.Filter);
  } else {
    this.dataSource.filterColumnValue(filter);
    }
  }

  applyEmptyFilter() {
     const emptyFilter = {
        columns: []
      };
      
      this.applyFilter(emptyFilter);
  }

  async updateNoDataMessages()
  {
    const itemName = await lastValueFrom(this.translateService.get(this.itemName != '' ? this.itemName : "table.item_default")) as string;

    this.textNoSearchResults = this.translateService.instant("table.no_search_results");

    this.textNoInitialDataResults = this.noDataMessage
        ? this.translateService.instant(this.noDataMessage, { itemName: itemName.toLowerCase() })
        : this.translateService.instant("table.no_data_row", { itemName: itemName.toLowerCase() });
  }

  getFooterData(_column: any) {
    return this.footerData;
  }

  getHeaderData(): HeaderMetatada[] {
    return this.columns.map((val) => {
      return {
        title: val.title,
        path: val.path,
      };
    });
  }

  checkIfRowHasUpdates(row: T){
    if(this.hasLiveUpdates && this.hasRowChanged){
      return this.hasRowChanged(row);
    }
    return false;
  }

  getExportHeaderData(): HeaderMetatada[] {
    return this.externalHeaderDataForExport ?? this.getHeaderData()
  }

  getValue(row: T, column: Column): unknown {
    const path = row.children ? column.path : column.metadata?.childPath ?? column.path
    return getValueAt(row, path)
  }

  tryGetTranslatedValue(row: T, column: Column): unknown {
    const rawValue = this.getValue(row, column)
    const translatedValue = column.metadata?.translationPrefixKey ?
        this.translateService.instant(`${column.metadata?.translationPrefixKey}.${camelToSnakeCase(rawValue as string)}`) : undefined;

    return translatedValue ?? rawValue;
  }

  get isInitalDataEmpty (){
    return this.dataSource.isInitialDataEmpty();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
  if (changes['lazyLoad'] && this.lazyLoad) {
    this.dataSource.enableLazyLoad();
  }
  if (changes['data'] && this.data) {
    this.onDataChange();
  }
  if (changes['columnDefinition']) {
    this.onColumnDefinitionChange();
  }
  if ((changes['columnDefinition'] || changes['shouldHaveItemOption']) && this.shouldHaveItemOption && this.columnDefinition) {
    this.columnNames.push(this.optionColumn.title);
  }
  if (changes['shouldHaveItemOption']?.previousValue && this.columnDefinition) {
    this.columnNames.pop();
  }
  if (changes['dragDropEnabled'] && this.columnDefinition) {
    this.onDragDropChanges(changes);
  }
  if (changes['itemName'] && changes['itemName'].currentValue) {
    await this.updateNoDataMessages();
  }
  if (changes['filterKey'] && this.enableFiltering) {
    if(!this.filterStateService.isValidKey(this.filterKey))
    {
      this.applyEmptyFilter();
    }
    else {
      this.latestFilter = this.filterStateService.loadFilter(this.filterKey);
      this.applyFilter(this.latestFilter);
    }   
  }
}

  private onDragDropChanges(changes: SimpleChanges) {
    if (changes['dragDropEnabled'].currentValue) {
      this.columnNames.unshift(this.dragColumn.title);
    } else if (this.columnNames[0] === this.dragColumn.title) {
      this.columnNames.shift();
    }
  }

  private onDataChange() {
    if (this.lazyLoad) {
      this.data = this.data.map(x => ({...x, loadingStatus: TableDataItemLoadingStatus.NotLoaded} as T))
    }
    this.updateDataSource(!this.shouldKeepStateWhenDataChange);
  }

  private onColumnDefinitionChange() {
    const propEntries = Object.entries<ColumnMetadata>(this.columnDefinition);
    this.columns = propEntries
        .filter((e) => !e[1].disable ?? true)
        .map((e) => {
          return {
            title: e[1].title,
            path: e[1].path ?? e[0],
            metadata: e[1],
            propKey: e[0],
          };
        });
    this.dataHeaders = this.columns.map((e) => e.title);
    this.columnNames = this.dataHeaders.slice();
    this.setExpansionColumn();
    this.dataSource.columnsDef = this.columns;
    this.dataSource.sortData();
  }

  updateDataSource(resetSort = true)
  {
    this.primaryCount = 0
    if (resetSort){
      this.latestSort = undefined;
    }
    this.dataSource.data = this.data;
    this.dataSource.sortData(this.latestSort);
    this.setExpansionColumn();
    if (this.dragDropEnabled && this.itemIndexToHoldFocus !== undefined){
      this.ngZone.onStable.pipe(first()).subscribe(() => {
        this.updateRowFocus(this.itemIndexToHoldFocus!)
        this.itemIndexToHoldFocus = undefined
      });
    }
  }

  setExpansionColumn(){
    if (this.shouldExpansionColumnBeAdded) {
      this.columnNames.unshift(this.expansionColumn.title);
    }
  }

  trackById(_index: number, item: T){
    return item.id;
  }

  responsiveClass(column: Column) {
    let className = "";
    if(column.metadata?.responsive){
      className = 'd-none d-'+column.metadata?.responsive+'-table-cell';
    }
    return className;
  }

  get shouldExpansionColumnBeAdded() {
    return this.dataSource.hasNestedItems && this.columnNames?.[0] !== this.expansionColumn.title
  }

  announceSortChange(sortState: Sort): void {
    this.latestSort = sortState
    if (this.externalPagination) {
      this._tableQuery = { ... this._tableQuery, sort: sortState} as TableQueryResult;
      this.emitQueryChangeEvent(this._tableQuery, TableQueryChangeEventType.Sorter);
    } else {
      this.dataSource.sortData(sortState);
    }
  }

  onDrop($event: CdkDragDrop<T[]>): void {
    this.dragDisabled = true;
    this.dataSource.moveItemInArray($event);
    this.dragDropDataChanged.emit(this.dataSource.data);
  }

  onPaginationChanged($event: Pagination): void {
    this.itemPerPage = $event.pageSize;
    this.currentPage = $event.currentPage;
    if (this.externalPagination) {
      const paginator = { pageNumber: $event.currentPage - 1, pageSize: $event.pageSize } as TablePaginator
      this._tableQuery = { ... this._tableQuery, paginator: paginator }
      this.emitQueryChangeEvent(this._tableQuery, TableQueryChangeEventType.Paginator);
    } else {
      this.dataSource.paginationChanged($event);
    }
  }

  filterButtonClicked(): void {
  let filterDialogData: TableFilterDialogData;

  if (this.externalPagination) {
    const filtersData = this.columns
      .filter(column => column.metadata?.filterable === true)
      .map(column => {
        const filteredColumn = this._tableQuery?.filterResult?.columns.find(fc => fc.column.title === column.title);
        const preSelectedOptions = column.metadata?.filter?.filterOptions?.filter(option => {
          return !!filteredColumn?.value.includes(option.value);
        }) ?? [];

        return {
          column: column,
          preSelectedOptions: [...preSelectedOptions]
        } as TableFilterDialogColumnData;
      });

    filterDialogData = { columns: filtersData } as TableFilterDialogData;
  } else {
    const originalDialogData = this.dataSource.getFilterDialogData();
    filterDialogData = {
      ...originalDialogData,
      columns: originalDialogData.columns.map(col => ({
        ...col,
        preSelected: Array.isArray(col.preSelected) ? [...col.preSelected] : col.preSelected
      }))
    };
  }
  filterDialogData.title = this.customFilterTitle ? this.customFilterTitle : this.title;
  filterDialogData.description = this.filterDialogDescription;
  filterDialogData.buttonTitle = this.customMainButtonTitle

  const filterDialogRef = this.dialog.open(ValgFilterDialogComponent, {
    data: filterDialogData,
    restoreFocus: true
  });

  filterDialogRef.afterClosed().subscribe((filter: TableFilterDialogResult | undefined) => {
    if (filter !== undefined) {
      this.applyFilter(filter);
    }
  });
}

  buildCprString(searchTerm: string): string {
    return searchTerm.replace(/\d{7,10}/, (st) => {
      const cprFirstHalf = "" + /\d{6}/.exec(st);
      const cprSecondHalf = st.replace(cprFirstHalf, "");
      return cprFirstHalf + "-" + cprSecondHalf;
    });
  }

  onSearchInputChange(eventTarget: EventTarget | null, searchField: TableSearchField | undefined = undefined): void {
    let filter = (eventTarget as HTMLInputElement).value;
    let processedSearchTerm = (filter?.trim()) || '';
    if (cprPattern.test(processedSearchTerm)) {
      processedSearchTerm = this.buildCprString(processedSearchTerm);
    }

    this._tableQuery = { ...this._tableQuery, searchValues: {
        ...this._tableQuery?.searchValues,
        [searchField?.discriminator as string]: processedSearchTerm
      }
    } as TableQueryResult;
    if (this.lazySearch) {
      return;
    }
    this.searchUsed = processedSearchTerm == '' ? false : true;
    if (this.externalPagination) {
      this._delayedSearch(() => {
        this.emitQueryChangeEvent(this._tableQuery, TableQueryChangeEventType.Searcher)
      })
    } else if (searchField) {
      this.dataSource.multipleSearch(processedSearchTerm, searchField);
    } else {
      this.dataSource.search(processedSearchTerm);
    }
  }

  onSearchClick(eventTarget: EventTarget | null, searchField: TableSearchField | undefined = undefined) {
    let filter = (eventTarget as HTMLInputElement).value;
    let processedSearchTerm = (filter?.trim()) || '';
    this.searchUsed = processedSearchTerm == '' ? false : true;

    if (cprPattern.test(processedSearchTerm)) {
      processedSearchTerm = this.buildCprString(processedSearchTerm);
    }

    if (this.externalPagination) {
      this.emitQueryChangeEvent(this._tableQuery, TableQueryChangeEventType.Searcher)
    } else if (searchField) {
      this.dataSource.multipleSearch(processedSearchTerm, searchField);
    } else {
      this.dataSource.search(processedSearchTerm);
    }
  }

  clearSearch(searchField: TableSearchField | undefined = undefined) {
    this.searchUsed = false;
    this._tableQuery = { ...this._tableQuery, searchValues: {
        ...this._tableQuery?.searchValues,
        [searchField?.discriminator as string]: ''
      }
    } as TableQueryResult;
    this.emitQueryChangeEvent(this._tableQuery, TableQueryChangeEventType.Searcher);
  }

  onMainActionClicked(): void {
    this.mainActionEventEmitter.emit();
  }

  onSecondaryActionClicked(): void {
    this.secondaryActionEventEmitter.emit();
  }

  printButtonClicked(): void {
    if(this.printHandlerEmitter)
    {
      this.printHandlerEmitter.emit(this.dataSource.dataStream.value);
    }
  }
  refreshButtonClicked(): void {
    this.refreshEventEmitter.emit();
  }

  hasAction(): boolean {
    return this._secondaryActions.length > 0;
  }

  public getPresentationComponent(
    column: Column
  ): Type<CellRendererComponent> {
    let presentationComponent = column.metadata?.presentationComponent;
    let childPresentationComponent = column.metadata?.childPresentationComponent;
    return presentationComponent ?? childPresentationComponent ?? TextEllipsisTooltipRendererComponent;
  }

  public shouldShowChildRowCount(data: T){
    return this.dataSource.hasNestedItems && data.children && this.showChildrenRowsCount;
  }

  public getChildRowCount(data: T, column: Column): number{
    return column.metadata?.countChild?.(data) ?? data.children?.length ?? 0;
  }

  public getAccessibilityId(data: T): string{
    let accessibilityIds = [];
    for (const column of this.columns) {
      if (column.metadata?.isAccessibilityId){
        accessibilityIds.push(this.getValue(data, column));
      }
    }
    if (accessibilityIds.length === 0){
      accessibilityIds.push(this.getValue(data, this.columns[0]))
    }
    return accessibilityIds.join(" ");
  }
  public getItemOption(data: T){
    return this.itemOptions.filter( x => {
      return x.filter === undefined || x.filter(data);
    })
  }


  public getProcessedExportData(): T[]{
    return this.dataSource.getProcessedExportData();
  }

  toggleChildrenRows($event: MouseEvent, row: T) {
    const target = $event.target as HTMLElement;
    const parentRow = target.closest('tr');
    if(parentRow) {
      this.toggleChildRow(parentRow, undefined, row);
    }
  }

  toggleAllChildrenRows($event: MouseEvent) {
    if (this.lazyLoad) {
      this.dataSource.updateNestedDataAndRefresh(true);
    }
    const target = $event.target as HTMLElement;
    const header = target.closest('th')
    if (header) {
      const parentRowsElement: HTMLElement[] = Array.from(this._tableContainer.nativeElement.querySelectorAll('.parent-row'));
      const closedRow = parentRowsElement.filter(row => row.dataset['expanded'] === 'false');
      const isAnyClosedRow = closedRow?.length > 0;
      if (isAnyClosedRow) {
        header.dataset['expanded'] = 'true';
        this.renderer.setAttribute(target, 'aria-expanded', 'true' );
        closedRow.forEach(row => { this.toggleChildRow(row, false) })
      } else {
        header.dataset['expanded'] = 'false';
        this.renderer.setAttribute(target, 'aria-expanded', 'false' );
        parentRowsElement.forEach(row => { this.toggleChildRow(row, true) })
      }
    }
  }

  private toggleChildRow(parentRow: HTMLElement, isOpen: boolean | undefined = undefined, row: T | undefined = undefined) {
    parentRow.dataset['expanded'] = isOpen ?? parentRow.dataset['expanded'] === 'true' ? 'false' : 'true';
    if (row?.loadingStatus === TableDataItemLoadingStatus.NotLoaded) {
      row.loadingStatus = TableDataItemLoadingStatus.Loading;
      this.dataSource.updateNestedDataAndRefresh()
    } else {
      let currentElement = parentRow.nextElementSibling as HTMLDivElement;
      let button = parentRow.getElementsByClassName('expandable-button');
      while(currentElement?.classList.contains('child-row')) {
        if(button.length !== 0){
          this.renderer.setAttribute(button[0], 'aria-expanded', parentRow.dataset['expanded']);
        }
        if (parentRow.dataset['expanded'] === 'true') {
          this.renderer.removeClass(currentElement, 'display-hidden')
        } else {
          this.renderer.addClass(currentElement, 'display-hidden')
        }
        currentElement = currentElement.nextElementSibling as HTMLDivElement;
      }
      this.cdr.detectChanges()
    }
  }

  private emitQueryChangeEvent(query: TableQueryResult | undefined, eventType: TableQueryChangeEventType): void {
    if (this._afterViewInitialized && query) {
      this.queryChangeEvent.emit({ tableQueryResult: query, eventType:  eventType});
    }
  }

  private constructDefaultQuery() {
    const defaultSortColumn = this.columns.find(column => column.metadata?.defaultSort != undefined);
    if (defaultSortColumn) {
      this._tableQuery = { ... this._tableQuery, sort: {
          id: defaultSortColumn.metadata?.sortDiscriminator!,
          sortDiscriminator: defaultSortColumn.metadata?.sortDiscriminator,
          sortDirection: defaultSortColumn.metadata?.defaultSort,
        }} as TableQueryResult
    }
    this.emitQueryChangeEvent(this._tableQuery, TableQueryChangeEventType.Initializer)
  }

  moveRowUp(index: number, comp: OverflowMenuComponent){
    const newIndex = index-1;
    this.dataSource.moveItemInArray(null, index, newIndex);
    this.dragDropDataChanged.emit(this.dataSource.data);
    comp.close()
    this.itemIndexToHoldFocus = newIndex
    this.ngZone.onStable.pipe(first()).subscribe(() => {
      this.updateRowFocus(newIndex);
      comp.open()
    });
  }

  moveRowDown(index: number, comp: OverflowMenuComponent){
    const newIndex = index+1;
    this.dataSource.moveItemInArray(null, index, newIndex);
    this.dragDropDataChanged.emit(this.dataSource.data);
    comp.close()
    this.itemIndexToHoldFocus = newIndex
    this.ngZone.onStable.pipe(first()).subscribe(() => {
      this.updateRowFocus(newIndex)
      comp.open()
    });
  }

  updateRowFocus(index: number){
    const rows: HTMLElement[] = this._tableContainer.nativeElement.querySelectorAll('.cdk-row');
    if (index < rows.length){
      rows[index].querySelector('.drag-and-drop-button')?.closest('button')?.focus();
    }
  }

  isLoadingDone(row: TableDataItem) {
    if (this.lazyLoad) {
      return row.loadingStatus === 'Done';
    } else {
      return false;
    }
  }

  public setPagination(page : number){
    this._tablePaginator.currentPage = page;
  }

  public shouldHidePagination(){
    return (this.totalItems ?? this.itemCount) <= this.itemPerPage;
  }

  public onSearchFieldFocus(eventTarget: EventTarget | null, currentSearchField: TableSearchField) {
    if(currentSearchField.barcodeScannerProcessing && this.searchInputs){
      this.tryListenToScanner(eventTarget,currentSearchField);
    }
  }

  public onSearchFieldFocusOut(currentSearchField: TableSearchField) {
    if(currentSearchField.barcodeScannerProcessing){
      this.tryDisableScanner();
    }
  }

  private tryListenToScanner(eventTarget: EventTarget | null,currentSearchField: TableSearchField) {

    const barcodeEvent = fromEvent(this.barCodeScanner, 'scan');
    this.barcodeScannerSubscription = barcodeEvent.subscribe((e: Event) => {
      if (!this.searchInputs) {
        return;
      }
      const customEvent = e as CustomEvent<string>;
      const rawScanned = customEvent.detail;

      const processed = currentSearchField.barcodeScannerProcessing!(rawScanned);

      if (!processed) {
        return;
      }

      const searchFieldInputElementMap = new Map();
      this.searchFields.forEach((searchField, index) => {
        if(this.searchInputs !== undefined){
          const correspondingInputElement = this.searchInputs.toArray()[index];
          searchFieldInputElementMap.set(searchField.discriminator, correspondingInputElement);
        }
      });

      const targetSearchField = this
      .searchFields
      .find(x => x.discriminator === currentSearchField.barcodePatchTarget);

      if(!targetSearchField){
        return;
      }

      const targetInputElement = searchFieldInputElementMap.get(targetSearchField.discriminator);

      const scannedInputElement = (eventTarget as HTMLInputElement);

      if (currentSearchField.discriminator !== targetSearchField.discriminator) {
        scannedInputElement.value = "";
      }

      targetInputElement.nativeElement.value = processed;

      this._tableQuery = { ...this._tableQuery, searchValues: {
        ...this._tableQuery?.searchValues,
        [targetSearchField.discriminator]: processed,
        [currentSearchField.discriminator]: targetSearchField.discriminator !== currentSearchField.discriminator ? "" : processed,
    }
    } as TableQueryResult

      this.emitQueryChangeEvent(this._tableQuery, TableQueryChangeEventType.Searcher);
    });
  }

  private tryDisableScanner(){
    if(this.barcodeScannerSubscription)
    {
      this.barcodeScannerSubscription.unsubscribe();
    }
  }

}
